import { apiClient } from '@api/apiClient';
import {
  mapStopFacilityDetails,
  normalizeFacilityDetails,
  serializeFacilityDetails,
} from '@common/facilityDetails';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components/Toast/Toast';
import { LTLLoadItem, LTLQuoteState } from '@store/ltl/types';
import {
  setCustomerInfo,
  setLoadItems,
  setLtlQuoteState,
  setOriginOrDestinationInfo,
  setPickupDate,
} from '@store/ltl/store';
import { loadCustomerInfo } from '@store/orders/actions/orderCustomerActions';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handlePopupToast, handleToast } from '@utils/utils';
import { produce } from 'solid-js/store';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { orderStore } from '@store/orders/store';

import { buildSourceTrackingConfig } from './serviceHelpers';

export const testCall404 = async () => {
  try {
    await apiClient.post('/this/will/fail');
    return true;
  } catch (error: unknown) {
    logError('testCall404', error as Error);
    handleToast(ToastType.Error, 'Failed to Change Rate Confirmation Status');
  }
};

export async function fetchOrderLoadboard(orderId: string) {
  try {
    const response: { value: IOrderViewModel } = await apiClient.get(
      `/order/${orderId}`,
    );
    const loads = response.value.loads.map((load) => {
      return {
        ...load,
        stops:
          load.stops?.map((s) => {
            return {
              ...s,
              // driverInTime:
              //   s.driverInTime !== undefined && s.driverInTime !== null
              //     ? DateTime.fromISO(s.driverInTime, { zone: 'utc' })
              //         .toLocal()
              //         .toISO()
              //     : undefined,
              // driverOutTime:
              //   s.driverOutTime !== undefined && s.driverOutTime !== null
              //     ? DateTime.fromISO(s.driverOutTime, { zone: 'utc' })
              //         .toLocal()
              //         .toISO()
              //     : undefined,
            };
          }) ?? [],
      };
    });
    const orderData = mapStopFacilityDetails(
      {
        ...response.value,
        loads,
      },
      normalizeFacilityDetails,
    );
    SetLTLQuoteRelatedData(orderData);
    if (orderData.customerId !== null) {
      await loadCustomerInfo(orderData.customerId);
    }

    return orderData;
  } catch (error: unknown) {
    logError('fetchOrderLoadboard', error as Error);
    throw createError(error, 'Failed to fetch order for loadboard');
  }
}

function SetLTLQuoteRelatedData(orderData: IOrderViewModel) {
  // the load array may not be there, lets check the length
  if (
    orderData.loads.length > 0 &&
    orderData.loads[0].quoteId !== null &&
    orderData.loads[0].quoteId !== undefined
  ) {
    setLtlQuoteState(
      produce((draft: LTLQuoteState) => {
        if (draft.quote) {
          draft.quote.ltlQuote = orderData.loads[0].ltlQuote;
        } else {
          draft.quote = { ltlQuote: orderData.loads[0].ltlQuote };
        }
      }),
    );
    if (
      orderData.customerId !== 0 &&
      orderData.customerId !== null &&
      orderData.customerName !== undefined
    ) {
      setCustomerInfo(orderData.customerId, orderData.customerName);
    }

    if (orderData.loads[0].items !== undefined) {
      setLoadItems(
        orderData.loads[0].items.map((item) => {
          return {
            lengthInch: item.lengthInch,
            widthInch: item.widthInch,
            heightInch: item.heightInch,
            weight: item.weight,
            quantity: item.quantity,
            type: item.item,
            uom: 'lbs',
            item: item.description,
            class: Number(item.class),
            suggestedClass: item.suggestedClass,
            nmfc: item.nmfc ?? '',
            description: item.description,
            hazmat: item.hazmat,
            loadItemHazmatDetail: item.loadItemHazmatDetail,
            pieces: Number(item.pieces),
          };
        }) as unknown as LTLLoadItem[],
      );
    }
    const pickupStop = orderData.loads[0]?.stops?.find(
      (x) => x.pickUp === true,
    );
    const dropoffStop = orderData.loads[0]?.stops?.find(
      (x) => x.pickUp === false,
    );
    if (pickupStop) {
      setOriginOrDestinationInfo({
        type: 'Origin',
        city: pickupStop.city,
        state: pickupStop.state,
        zip: pickupStop.zip,
        country: pickupStop.countryCode,
      });
      if (
        pickupStop.stopDateTime !== undefined &&
        pickupStop.stopDateTime !== null
      ) {
        setPickupDate(pickupStop.stopDateTime);
      }
    }
    if (dropoffStop) {
      setOriginOrDestinationInfo({
        type: 'Destination',
        city: dropoffStop.city,
        state: dropoffStop.state,
        zip: dropoffStop.zip,
        country: dropoffStop.countryCode,
      });
    }
  }
}

export async function fetchOrder(orderId: string): Promise<IOrderViewModel> {
  try {
    const response: V4ApiResponse<IOrderViewModel> = await apiClient.get(
      `/order/${orderId}`,
    );
    let orderData = response.value as unknown as IOrderViewModel;
    //Note: setting the bid type to Spot Market if it is null
    orderData = {
      ...orderData,
      bidType: Boolean(orderData.bidType) ? orderData.bidType : 'Spot Market',
    };

    SetLTLQuoteRelatedData(orderData);

    return mapStopFacilityDetails(orderData, normalizeFacilityDetails);
  } catch (error: unknown) {
    logError('fetchOrder', error as Error);
    if (error instanceof Error) {
      throw new Error(`Failed to fetch offices: ${error.message}`);
    }
    throw new Error(`Failed to fetch offices: ${error}`);
  }
}

export async function fetchCopyOrder(
  copyOrderId: string,
): Promise<IOrderViewModel> {
  try {
    const response: V4ApiResponse<IOrderViewModel> = await apiClient.get(
      `/order/GetCopyOrder/${copyOrderId}`,
    );
    let orderData = response.value as IOrderViewModel;
    //Note: setting the bid type to Spot Market if it is null
    orderData = {
      ...orderData,
      loads: orderData.loads.map((load) => {
        return {
          ...load,
          stops: load.stops?.map((stop) => {
            return {
              ...stop,
              stopDateTime: stop.dropOff
                ? undefined
                : stop.stopDateTime !== null &&
                    stop.stopDateTime !== undefined &&
                    stop.stopDateTime.toString() !== '0001-01-01T00:00:00'
                  ? stop.stopDateTime
                  : undefined,
            };
          }),
        };
      }),
      bidType: Boolean(orderData.bidType) ? orderData.bidType : 'Spot Market',
    };
    if (orderData.customerId !== null) {
      await loadCustomerInfo(orderData.customerId);
    }

    SetLTLQuoteRelatedData(orderData);

    return mapStopFacilityDetails(orderData, normalizeFacilityDetails);
  } catch (error: unknown) {
    logError('fetchOrder', error as Error);

    if (error instanceof Error) {
      throw new Error(`Failed to fetch offices: ${error.message}`);
    }
    throw new Error(`Failed to fetch offices: ${error}`);
  }
}

type OrderStop = NonNullable<IOrderViewModel['loads'][number]['stops']>[number];

const addressIdFromStop = (stop: OrderStop | undefined): string | null => {
  const addressId = stop?.facilityDetails?.customerAddressId;
  if (addressId == null || addressId.length === 0) return null;
  return addressId;
};

const addressIdForSavedStop = (
  sourceStops: OrderStop[],
  savedStop: OrderStop,
  savedIndex: number,
  sameLength: boolean,
): string | null => {
  const byId = sourceStops.find((stop) => stop.id === savedStop.id);
  if (byId != null) return addressIdFromStop(byId);

  const byOrder = sourceStops.find(
    (stop) =>
      stop.operationType !== 'Delete' &&
      savedStop.stopOrder != null &&
      stop.stopOrder === savedStop.stopOrder,
  );
  if (byOrder != null) return addressIdFromStop(byOrder);

  if (!sameLength) return null;
  return addressIdFromStop(sourceStops[savedIndex]);
};

const withCustomerAddressLinks = (
  source: IOrderViewModel,
  saved: IOrderViewModel,
): IOrderViewModel => ({
  ...saved,
  loads: saved.loads.map((load, loadIndex) => {
    const sourceStops = source.loads[loadIndex]?.stops ?? [];
    const savedStops = load.stops ?? [];
    const sameLength = sourceStops.length === savedStops.length;
    return {
      ...load,
      stops: savedStops.map((stop, stopIndex) => {
        const addressId = addressIdForSavedStop(
          sourceStops,
          stop,
          stopIndex,
          sameLength,
        );
        if (addressId == null || stop.facilityDetails == null) return stop;
        return {
          ...stop,
          facilityDetails: {
            ...stop.facilityDetails,
            customerAddressId: addressId,
          },
        };
      }),
    };
  }),
});

export async function saveOrder(
  order: IOrderViewModel,
  changedForms: readonly ChangeTrackingSource[] = [],
): Promise<IOrderViewModel> {
  try {
    order.loads
      .filter((load: ILoadEditViewModel) => load.mode === 'LTL')
      .forEach((load) => {
        load.newLtlQuoteId = orderStore.quoteId as unknown as number;
      });
    delete order.loads[0].ltlQuote;
    const requestConfig = buildChangeTrackingRequestConfig(
      order.id === 0
        ? [CHANGE_TRACKING_SOURCES.orderDetails.orderCreate]
        : changedForms,
    );
    const requestOrder = mapStopFacilityDetails(
      order,
      serializeFacilityDetails,
    );
    const updatedOrder: IOrderViewModel =
      order.id === 0
        ? await apiClient.post('order', requestOrder, requestConfig)
        : await apiClient.put('order', requestOrder, requestConfig);
    if (updatedOrder.id) {
      handlePopupToast(ToastType.Success, `Order saved #${updatedOrder.id}`);
    } else {
      handleToast(ToastType.Error, 'Failed to create Order please try again');
    }

    return withCustomerAddressLinks(
      order,
      mapStopFacilityDetails(
        updatedOrder,
        normalizeFacilityDetails,
      ) as unknown as IOrderViewModel,
    );
  } catch (error: unknown) {
    logError('saveOrder', error as Error);
    throw createError(error, 'Failed to save order');
  }
}

export async function voidOrder(
  orderId: number,
  changeTrackingSource?: ChangeTrackingSource,
) {
  try {
    const response: { isSuccess: boolean; message: string } =
      await apiClient.post(
        `Order/Void/${orderId}`,
        undefined,
        buildSourceTrackingConfig(changeTrackingSource),
      );

    handleToast(
      Boolean(response.isSuccess) ? ToastType.Success : ToastType.Error,
      Boolean(response.isSuccess) ? 'Order Voided' : response.message,
    );
    return response;
  } catch (error: unknown) {
    logError('voidOrder', error as Error);
    throw createError(error, 'Failed to void order');
  }
}

export async function postCopiedOrder(
  id: string,
  copiedOrder: IOrderViewModel,
) {
  try {
    const payload = { id, order: copiedOrder };
    await apiClient.post(
      '/order/StoreCopyOrder',
      payload,
      buildChangeTrackingRequestConfig([
        CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderCopyOrder,
      ]),
    );
  } catch (error) {
    logError('postCopiedOrder', error as Error);
    handleToast(ToastType.Error, 'Failed to copy order');
    throw new Error(`Failed to copy order for id: ${id}`);
  }
}
